import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import { 
  Settings,
  Logout,
  Feedback,
  School,
  Description,
  WorkspacePremium,
  KeyboardArrowDown
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { getErrorMessage } from "../../error-helpers/errorMessage";
import LoadingPage from "../../common/LoadingPage";
import { darkTheme } from "../../theme";
import { useUser, useClerk } from "@clerk/clerk-react";
import SubscriptionModal from "../subscription/SubscriptionModal";
import TimeSavingsWidget from "../TimeSavingsWidget";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { updateUser } from "../../store/userSlice"; // Import the Redux action to update the user

const API_URL = process.env.REACT_APP_API_URL;

const NavBar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const clerk = useClerk();
  const storeUser = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch();

  // Track user profile changes
  const [prevUserName, setPrevUserName] = useState<string | null>(null);

  // Listen for user profile changes
  useEffect(() => {
    if (user) {
      const currentName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      
      // Only run this effect when the name has been loaded for the first time
      if (prevUserName === null) {
        setPrevUserName(currentName);
        return;
      }
      
      // Check if the name has changed
      if (prevUserName !== currentName && prevUserName !== null) {
        console.log('User name changed from', prevUserName, 'to', currentName);
        
        // Update the name in our backend
        updateUserNameInBackend(user.firstName || '', user.lastName || '');
        
        // Update the previous name
        setPrevUserName(currentName);
      }
    }
  }, [user?.firstName, user?.lastName]);

  // Function to update the user name in our backend
  const updateUserNameInBackend = async (firstName: string, lastName: string) => {
    try {
      // Get authentication token from Clerk
      if (!clerk.session) {
        console.error("No active session found");
        return;
      }
      const token = await clerk.session.getToken();
      
      // Call our backend API to update the user name
      const response = await fetch(`${API_URL}/users/update-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Clerk-User-Id': user?.id || '',
          'X-Clerk-Email': user?.primaryEmailAddress?.emailAddress || '',
          'X-Clerk-First-Name': firstName,
          'X-Clerk-Last-Name': lastName
        },
        body: JSON.stringify({ firstName, lastName })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update user name: ${response.statusText}`);
      }
      
      // Update the user in Redux store
      const updatedUserData = await response.json();
      dispatch(updateUser(updatedUserData));
      
      showSuccessToast('Your profile has been updated');
    } catch (error) {
      console.error('Error updating user name:', error);
      showErrorToast(`Failed to update profile: ${getErrorMessage(error)}`);
    }
  };

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setDrawerOpen(open);
    };

  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false); // Close drawer after navigation
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    setLogoutDialogOpen(false);
    try {
      // Logout with Clerk
      await clerk.signOut();
      navigate("/");
    } catch (error) {
      showErrorToast(`Error logging out: ${getErrorMessage(error)}`);
      console.error('Logout error:', error);
    }
  };

  const handleOpenSubscriptionModal = () => {
    handleProfileMenuClose(); // Cierra el menú del perfil
    setShowSubscriptionModal(true);
  };

  const handleOpenClerkSettings = () => {
    handleProfileMenuClose(); // Close the profile menu
    clerk.openUserProfile(); // Open Clerk's user profile modal
  };

  // Rearranged drawer items
  const drawerItems = [{ text: "Dashboard", path: "/dashboard" }];
  const textColor = "#ffffff";

  // Obtener la primera letra del nombre o email para el avatar
  const getAvatarText = () => {
    if (user?.firstName) return user.firstName.charAt(0).toUpperCase();
    if (user?.primaryEmailAddress) return user.primaryEmailAddress.emailAddress.charAt(0).toUpperCase();
    return "U";
  };

  // Subscription status
  const isPro = storeUser?.hasActiveSubscription;

  return (
    <>
      <AppBar
        position="static"
        color="primary"
        sx={{
          backgroundColor: darkTheme.palette.background.default,
          color: "#333333",
        }}
      >
        {/*
        <IconButton
          edge="start"
          disabled={true}
          color="inherit"
          aria-label="menu"
          onClick={toggleDrawer(true)}
        >
          <MenuIcon sx={{ color: textColor }} />
        </IconButton>*/}

        <Toolbar>
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              color: textColor, 
              cursor: 'pointer' 
            }}
            onClick={() => navigate('/dashboard')}
          >
            tranzcribe
          </Typography>

          {/* Time Savings Widget */}
          <TimeSavingsWidget isPro={isPro} />

          {/* Pro Subscription Button - Only shown for non-pro users */}
          {!isPro && storeUser && storeUser.hasActiveSubscription === false && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowSubscriptionModal(true)}
              sx={{
                bgcolor: '#5D5FEF',
                color: 'white',
                mr: 2,
                ml: 2,
                px: 2,
                py: 0.75,
                borderRadius: 1.5,
                fontSize: '0.875rem',
                fontWeight: 'bold',
                textTransform: 'none',
                // Mobile-specific styling
                my: { xs: 1, sm: 0 }, // Add top and bottom margin on mobile
                boxShadow: { xs: '0 4px 12px rgba(93, 95, 239, 0.4)', sm: 'none' }, // Eye-catching shadow on mobile
                '&:hover': {
                  bgcolor: '#4A4BD5',
                  boxShadow: { xs: '0 6px 16px rgba(93, 95, 239, 0.5)', sm: 'none' }
                }
              }}
            >
              Get Unlimited with Pro
            </Button>
          )}

          {/* Profile Button with User Info */}
          <Button
            color="inherit"
            onClick={handleProfileMenuOpen}
            sx={{
              textTransform: 'none',
              color: "#ffffff",
              display: 'flex',
              alignItems: 'center',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
            endIcon={<KeyboardArrowDown />}
          >
            <Box sx={{ position: 'relative' }}>
              <Avatar 
                sx={{ 
                  bgcolor: darkTheme.palette.secondary.main,
                  width: 32,
                  height: 32,
                  mr: 1
                }}
              >
                {user?.imageUrl ? (
                  <img src={user.imageUrl} alt={getAvatarText()} style={{ width: '100%', height: '100%' }} />
                ) : (
                  getAvatarText()
                )}
              </Avatar>
              {isPro && (
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    bottom: -8, 
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: '#5D5FEF', 
                    color: 'white',
                    fontSize: '8px',
                    fontWeight: 'bold',
                    borderRadius: '3px',
                    px: 0.5,
                    py: 0.1,
                    border: '1px solid rgba(0,0,0,0.1)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    zIndex: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  PRO
                </Box>
              )}
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'left' }}>
                {user?.firstName || 'User'}
              </Typography>
            </Box>
          </Button>

          {/* Profile Menu */}
          <Menu
            anchorEl={profileAnchorEl}
            open={Boolean(profileAnchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                width: 300,
                mt: 1.5,
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                }
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* User Info Section */}
            <Box sx={{ px: 2, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar 
                    src={user?.imageUrl} 
                    sx={{ width: 40, height: 40, mr: 1.5 }}
                  >
                    {getAvatarText()}
                  </Avatar>
                  {isPro && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        bottom: -8, 
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: '#5D5FEF', 
                        color: 'white',
                        fontSize: '8px',
                        fontWeight: 'bold',
                        borderRadius: '3px',
                        px: 0.5,
                        py: 0.1,
                        border: '1px solid rgba(0,0,0,0.1)',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        zIndex: 2,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      PRO
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {user?.firstName} {user?.lastName}
                  </Typography>
                </Box>
              </Box>
              
              {/* Subscription Badge */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mt: 1.5, 
                  p: 1,
                  bgcolor: isPro ? 'rgba(93, 95, 239, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                  borderRadius: 1
                }}
              >
                <WorkspacePremium 
                  sx={{ 
                    fontSize: 20, 
                    color: isPro ? '#5D5FEF' : '#64748b',
                    mr: 1 
                  }} 
                />
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: isPro ? '#5D5FEF' : '#64748b' 
                    }}
                  >
                    {isPro ? 'Pro Plan' : 'Free Plan'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isPro 
                      ? 'You have unlimited transcriptions' 
                      : `Limited to ${process.env.REACT_APP_FREE_TRANSCRIPTION_LIMIT || '5'} transcriptions`
                    }
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider />
            
            {/* Menu Items */}
            <MenuItem onClick={handleOpenClerkSettings} dense>
              <Settings sx={{ mr: 2, fontSize: 20 }} />
              Account Settings
            </MenuItem>
            
            <MenuItem onClick={handleOpenSubscriptionModal} dense>
              <WorkspacePremium sx={{ mr: 2, fontSize: 20 }} />
              Manage Subscription
            </MenuItem>
            
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate("/feedback");
            }} dense>
              <Feedback sx={{ mr: 2, fontSize: 20 }} />
              Send Feedback
            </MenuItem>
            
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate("/tutorial");
            }} dense>
              <School sx={{ mr: 2, fontSize: 20 }} />
              Tutorial
            </MenuItem>
            
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate("/tos");
            }} dense>
              <Description sx={{ mr: 2, fontSize: 20 }} />
              Terms & Conditions
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={handleLogout} dense sx={{ color: 'error.main' }}>
              <Logout sx={{ mr: 2, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>

          {/* Old Menu (Keeping for reference, can be removed) */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{ display: 'none' }} // Hide the old menu
          >
            <MenuItem
              onClick={() => {
                navigate("/feedback");
                handleMenuClose();
              }}
            >
              Feedback
            </MenuItem>
            {/* Rearranged menu items */}
            <MenuItem
              onClick={() => {
                navigate("/tutorial");
                handleMenuClose();
              }}
            >
              Tutorial
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate("/tos");
                handleMenuClose();
              }}
            >
              Terms & Conditions
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Side Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            width: 250,
            backgroundColor: darkTheme.palette.background.default,
            color: darkTheme.palette.text.primary,
            height: "100%",
          }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {drawerItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton onClick={() => handleNavigate(item.path)}>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout?
          </DialogContentText>
          {isLoggingOut && <LoadingPage />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLogout} autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de configuración de suscripción */}
      <SubscriptionModal 
        open={showSubscriptionModal} 
        onClose={() => setShowSubscriptionModal(false)} 
      />
    </>
  );
};

export default NavBar;
